import { TIME_OFF_LABEL_MAX, type TimeOff, type ToolOutput, timeOffInputSchema } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { CalendarPlusIcon, Trash2Icon } from 'lucide-react'
import { type FormEvent, type RefObject, useEffect, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { ApiError } from '@/lib/api'
import { formatDayRange } from '@/lib/format'
import { queryKeys, useRunTool, useSettings, useTimeOff, useTimeOffSuggestions } from '@/lib/items'
import { timeOffState, timeOffText, userToday, visibleTimeOff } from '@/lib/plan'
import { keepInView } from '@/lib/scroll'
import { useUserNow } from '@/lib/session'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { Parts } from './parts'
import { TimeOffSuggestionView } from './time-off-suggestion'

type TimeOffList = ToolOutput<'time_off.list'>

/**
 * Whole days off, first and last day included. "To" follows "From" until it is set (a single
 * day needs one date); Enter in any field adds the period.
 */
function AddTimeOff({ fromRef }: { fromRef: RefObject<HTMLInputElement | null> }) {
  const { run } = useRunTool()
  const [from, setFrom] = useState('')
  /** null: the same day as From. */
  const [to, setTo] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const input = { startsOn: from, endsOn: to ?? from, label }
  const parsed = from ? timeOffInputSchema.safeParse(input) : null
  const error =
    parsed && !parsed.success
      ? (parsed.error.issues.find((issue) => issue.path[0] === 'endsOn')?.message ?? null)
      : null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!parsed?.success || saving) return
    setSaving(true)
    const result = await run('time_off.add', input, {
      done: 'Time off added',
      failed: 'Could not add time off',
    })
    setSaving(false)
    if (!result) return
    setFrom('')
    setTo(null)
    setLabel('')
  }

  return (
    <form
      id="add-time-off"
      aria-label="Add time off"
      className="flex scroll-mt-4 flex-col gap-3"
      onSubmit={(event) => void submit(event)}
      noValidate
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="time-off-from">From</Label>
          <Input
            ref={fromRef}
            id="time-off-from"
            type="date"
            value={from}
            onChange={(event) => {
              const next = event.target.value
              setFrom(next)
              // A last day before the new first day starts over from it.
              if (to !== null && next && to < next) setTo(null)
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="time-off-to">To</Label>
          <Input
            id="time-off-to"
            type="date"
            value={to ?? from}
            min={from || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'time-off-error' : undefined}
            onChange={(event) => setTo(event.target.value || null)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="time-off-label">
          Label <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <Input
          id="time-off-label"
          placeholder="e.g. Vacation"
          maxLength={TIME_OFF_LABEL_MAX}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>
      {error ? (
        <p id="time-off-error" role="alert" className="text-sm text-destructive">
          {error}.
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={!parsed?.success || saving}>
          {saving ? <Spinner aria-hidden="true" /> : <CalendarPlusIcon aria-hidden="true" />}
          Add time off
        </Button>
      </div>
    </form>
  )
}

/**
 * Settings → Time off: the periods hugo counts as not working (the last three that ended,
 * dimmed, then the current and upcoming ones), an add form, and remove after a confirmation.
 * `/settings/time-off#time-off` (the header's away chip) scrolls here; `#add-time-off` (the palette)
 * also puts the cursor in "From".
 */
export function TimeOffCard() {
  const periods = useTimeOff()
  const suggestions = useTimeOffSuggestions()
  const settings = useSettings()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const now = useUserNow(60_000)
  const { today, year } = userToday(now, settings.data?.timezone ?? 'Europe/Zurich')
  const [removing, setRemoving] = useState<{ period: TimeOff; open: boolean } | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const fromRef = useRef<HTMLInputElement>(null)
  const hash = useRouterState({ select: (s) => s.location.hash })

  useEffect(() => {
    if (hash !== 'time-off' && hash !== 'add-time-off') return
    const target = hash === 'time-off' ? cardRef.current : document.getElementById(hash)
    if (!target) return
    if (hash === 'add-time-off') fromRef.current?.focus({ preventScroll: true })
    return keepInView(target)
  }, [hash])

  const remove = async (period: TimeOff) => {
    setRemoving({ period, open: false })
    // Gone from the list right away; back if the call fails.
    await queryClient.cancelQueries({ queryKey: queryKeys.timeOff })
    const snapshot = queryClient.getQueryData<TimeOffList>(queryKeys.timeOff)
    queryClient.setQueryData<TimeOffList>(queryKeys.timeOff, (old) =>
      old ? { ...old, periods: old.periods.filter((p) => p.id !== period.id) } : old,
    )
    const result = await run(
      'time_off.remove',
      { id: period.id },
      { done: 'Time off removed', failed: 'Could not remove time off' },
    )
    if (!result && snapshot) queryClient.setQueryData(queryKeys.timeOff, snapshot)
  }

  const shown = periods.data ? visibleTimeOff(periods.data, today) : []
  const confirming = removing?.period
  return (
    <Card id="time-off" ref={cardRef} data-testid="time-off">
      <CardHeader>
        <CardTitle>Time off</CardTitle>
        <CardDescription>
          hugo counts these days as not working: it plans around them and helps you tell people who
          are waiting on you, or you on them.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {suggestions.data && suggestions.data.length > 0 ? (
          <section
            aria-labelledby="time-off-suggestions-heading"
            className="flex flex-col gap-2 rounded-lg border p-3"
            data-testid="time-off-suggestions"
          >
            <div className="flex flex-col gap-0.5">
              <h3 id="time-off-suggestions-heading" className="text-sm font-medium">
                From your calendar
              </h3>
              <p className="text-sm text-muted-foreground">
                Out-of-office time in Google Calendar that is not time off yet.
              </p>
            </div>
            <ul className="divide-y" aria-label="Suggested time off">
              {suggestions.data.map((suggestion) => (
                <TimeOffSuggestionView
                  key={suggestion.key}
                  suggestion={suggestion}
                  thisYear={year}
                  layout="row"
                />
              ))}
            </ul>
          </section>
        ) : null}
        {periods.data ? (
          shown.length > 0 ? (
            <ul className="divide-y" aria-label="Your time off">
              {shown.map((period) => {
                const state = timeOffState(period, today)
                const days = formatDayRange(period.startsOn, period.endsOn, year)
                return (
                  <li
                    key={period.id}
                    data-testid="time-off-period"
                    data-state={state}
                    className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <p
                      className={cn(
                        'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm',
                        state === 'ended' && 'text-muted-foreground',
                      )}
                    >
                      <span className="break-words" data-testid="time-off-text">
                        <Parts text={timeOffText(period, year)} />
                      </span>
                      {state === 'current' ? (
                        <Badge variant="outline" className={attention.badge}>
                          Now
                        </Badge>
                      ) : state === 'ended' ? (
                        <Badge variant="secondary" className="font-normal">
                          Ended
                        </Badge>
                      ) : null}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="max-sm:size-10"
                      aria-label={`Remove time off ${days}`}
                      title="Remove"
                      onClick={() => setRemoving({ period, open: true })}
                    >
                      <Trash2Icon aria-hidden="true" />
                    </Button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="time-off-empty">
              No time off planned.
            </p>
          )
        ) : periods.isError ? (
          <p className="text-sm text-destructive">
            Could not load your time off.{' '}
            {periods.error instanceof ApiError ? periods.error.message : ''}
          </p>
        ) : (
          <Skeleton className="h-12 w-full" />
        )}
        <Separator />
        <AddTimeOff fromRef={fromRef} />
      </CardContent>
      <AlertDialog
        open={removing?.open ?? false}
        onOpenChange={(open) => {
          if (!open) setRemoving((r) => (r ? { ...r, open: false } : r))
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove time off?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming
                ? `${formatDayRange(confirming.startsOn, confirming.endsOn, year)}${confirming.label ? ` (${confirming.label})` : ''} count as working days again, and hugo plans with them.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirming) void remove(confirming)
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
